import React, { useState } from 'react'
import { Button, Tonality } from '@dsny/dsny-component-library'
import { useTheme } from 'styled-components'
import { useTranslation } from 'react-i18next'
import { useDispatch, connect } from 'react-redux'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import ModalActions from 'reducers/Modal'
import { useMobileMediaQuery } from 'utils/hooks/useMediaQueries'
import {
  XButtonWrapper,
  ButtonWrapper
} from '../SignoutModal/SignoutModal.styles'
import { Close, Mailemailread } from '@dsny/dsny-component-library/icons'
import { StyledModal } from '../Modals.styles'
import { EmailIconBubble } from './ConnectEmailModal.styles'
import { thunks as profileThunks } from 'reducers/Profile'
import { LOADING_STATES } from 'constants/enums'

const mapStateToProps = ({ modal, profile }) => ({
  showConnectEmailModal: modal?.showConnectEmailModal,
  connectedEmails: profile?.profile?.emails || []
})

const schema = (connectedEmails) =>
  yup.object().shape({
    email: yup
      .string()
      .email('Please enter a valid email address')
      .required('Email is required')
      .notOneOf(
        connectedEmails,
        'This email is already associated with this account'
      )
  })

function ConnectEmailModal({ showConnectEmailModal, connectedEmails }) {
  const { t } = useTranslation()
  const theme = useTheme()
  const dispatch = useDispatch()
  const isMobile = useMobileMediaQuery()
  const [status, setStatus] = useState(LOADING_STATES.IDLE)

  const {
    handleSubmit,
    register,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(schema(connectedEmails))
  })

  const handleCancelButtonClick = () => {
    dispatch(ModalActions.toggleConnectEmailModal())
  }

  const onSubmit = async (data) => {
    try {
      await dispatch(profileThunks.startMergeEmail(data.email))
      setStatus(LOADING_STATES.SUCCESS)
    } catch (error) {
      console.error('Error merging email:', error)
      setStatus(LOADING_STATES.ERROR)
    }
  }

  return (
    <StyledModal
      $isMobile={isMobile}
      isOpen={showConnectEmailModal}
      onClose={handleCancelButtonClick}
      containerStyles={{
        padding: isMobile ? '24px 12px' : '72px 64px 40px'
      }}
    >
      <XButtonWrapper onClick={handleCancelButtonClick}>
        <Close width={24} height={24} color={theme.colors.neutralW80} />
      </XButtonWrapper>

      <div style={{ display: 'flex', flexDirection: 'row', gap: 16 }}>
        {status === LOADING_STATES.SUCCESS && (
          <EmailIconBubble>
            <Mailemailread width={24} />
          </EmailIconBubble>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
          <Tonality.UIHeading
            headingStyle="headline5"
            color={theme.colors.neutralW90}
            style={{ marginBottom: 8 }}
          >
            {status === LOADING_STATES.SUCCESS
              ? t('settings.confirmation-email-sent')
              : t('settings.connect-a-new-email')}
          </Tonality.UIHeading>

          <Tonality.UIText
            textStyle="body"
            color={theme.colors.neutralW70}
            style={{ fontSize: 14, marginBottom: 32 }}
          >
            {status === LOADING_STATES.SUCCESS
              ? t('settings.confirmation-email-sent-body')
              : t('settings.connect-a-new-email-body')}
          </Tonality.UIText>
        </div>
      </div>

      {status === LOADING_STATES.IDLE && (
        <form onSubmit={handleSubmit(onSubmit)}>
          <Tonality.TextField
            id="email"
            name="email"
            required
            setting="player"
            {...register('email')}
            ref={null}
            inputRef={register('email').ref}
            labelText={t('general.email')}
            placeholder={t('settings.enter-the-email-address')}
            error={errors.email?.message}
            inputStyle={{
              borderColor: errors.email?.message
                ? theme.colors.error70
                : theme.colors.neutralW50
            }}
            errorStyle={{
              color: theme.colors.error70,
              paddingTop: 5
            }}
            wrapperStyles={{ paddingRight: 0 }}
          />

          <ButtonWrapper style={{ gap: 16, marginTop: isMobile ? 16 : 32 }}>
            <Button
              type="submit"
              variant="primary"
              setting="player"
              mode="dark"
            >
              {t('settings.link-email')}
            </Button>

            <Button
              variant="secondary"
              setting="player"
              mode="dark"
              type="button"
              onClick={handleCancelButtonClick}
            >
              {t('general.cancel')}
            </Button>
          </ButtonWrapper>
        </form>
      )}
    </StyledModal>
  )
}

export default connect(mapStateToProps)(ConnectEmailModal)
